import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { ArrowLeft, Send, CheckCircle, AlertTriangle, Stethoscope, Brain, X, FileText, ChevronDown, ChevronUp, Download, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function IncidentReportPage({ group, onBack, mode = "counselor" }) {
  const isSpecialist = mode === "specialist";
  const isAthletics = mode === "athletics";
  const isPool = mode === "pool";
  // Modes that need the cross-group "pick a group, then a camper" picker
  // (no fixed counselor group to pull from).
  const useGroupPicker = isSpecialist || isAthletics || isPool;
  const [tab, setTab] = useState("new");
  const [pastReports, setPastReports] = useState([]);
  const [pastLoading, setPastLoading] = useState(false);
  const [expandedReport, setExpandedReport] = useState(null);
  const [campers, setCampers] = useState([]);
  const [selectedCampers, setSelectedCampers] = useState([]);
  const [camperSearch, setCamperSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rosterDate, setRosterDate] = useState("");
  const [specialty, setSpecialty] = useState("");
  // Specialist-only: which group's camper list is currently expanded for picking
  const [activeGroupCode, setActiveGroupCode] = useState("");
  // Specialist-only: daily roster for the active group on `form.date`
  const [activeRoster, setActiveRoster] = useState([]);
  const [activeRosterLoading, setActiveRosterLoading] = useState(false);

  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    great_day: false,
    great_day_notes: "",
    incident_type: "",
    description: "",
    actions_taken: [],
    parent_contact_detail: "",
    action_details: "",
    followup: [],
    followup_details: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (useGroupPicker) {
      // Specialist / Athletics / Pool need every camper across all groups
      axios.get(`${API}/campers`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setCampers(res.data || []))
        .catch(() => {});
    } else {
      const dateParam = rosterDate ? `&date=${rosterDate}` : "";
      axios.get(`${API}/roster/today?group_code=${group.code}${dateParam}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setCampers(res.data?.campers || []))
        .catch(() => {});
    }
  }, [group.code, rosterDate, useGroupPicker]);

  const fetchPastReports = async () => {
    setPastLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/incident-reports?group_code=${group.code}`, { headers: { Authorization: `Bearer ${token}` } });
      setPastReports(res.data || []);
    } catch { setPastReports([]); }
    setPastLoading(false);
  };

  useEffect(() => {
    fetchPastReports();
  }, [group.code]);

  // Specialist / Athletics / Pool: when a group is picked, pull THAT group's
  // daily roster for form.date so the picker reflects who's actually at camp.
  useEffect(() => {
    if (!useGroupPicker || !activeGroupCode) {
      setActiveRoster([]);
      return;
    }
    const token = localStorage.getItem("token");
    setActiveRosterLoading(true);
    axios.get(`${API}/roster/today?group_code=${activeGroupCode}&date=${form.date}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => setActiveRoster(res.data?.campers || []))
      .catch(() => setActiveRoster([]))
      .finally(() => setActiveRosterLoading(false));
  }, [useGroupPicker, activeGroupCode, form.date]);

  const toggleCamper = (camper) => {
    setSelectedCampers(prev => {
      const exists = prev.find(c => c.id === camper.id);
      if (exists) return prev.filter(c => c.id !== camper.id);
      return [...prev, camper];
    });
  };

  const toggleAction = (action) => {
    setForm(prev => ({
      ...prev,
      actions_taken: prev.actions_taken.includes(action)
        ? prev.actions_taken.filter(a => a !== action)
        : [...prev.actions_taken, action]
    }));
  };

  const toggleFollowup = (item) => {
    setForm(prev => ({
      ...prev,
      followup: prev.followup.includes(item)
        ? prev.followup.filter(f => f !== item)
        : [...prev.followup, item]
    }));
  };

  const handleSubmit = async () => {
    if (!form.great_day && !form.description) {
      toast.error("Please add a description of the incident");
      return;
    }
    if (!form.great_day && selectedCampers.length === 0) {
      toast.error("Please select at least one camper");
      return;
    }
    if (!form.great_day && !form.incident_type) {
      toast.error("Please select the type of incident");
      return;
    }
    if (isSpecialist && !form.great_day && !specialty.trim()) {
      toast.error("Please enter your specialty");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/incident-reports`, {
        ...form,
        camper_ids: selectedCampers.map(c => c.id),
        ...(isSpecialist && { specialty: specialty.trim() }),
      }, { headers: { Authorization: `Bearer ${token}` } });

      setSubmitted(true);
      toast.success("Incident report submitted!");
      fetchPastReports();

      // Reset after 2 seconds
      setTimeout(() => {
        setForm({
          date: new Date().toISOString().split("T")[0],
          great_day: false,
          great_day_notes: "",
          incident_type: "",
          description: "",
          actions_taken: [],
          parent_contact_detail: "",
          action_details: "",
          followup: [],
          followup_details: "",
        });
        setSelectedCampers([]);
        setSubmitted(false);
        setSpecialty("");
        setActiveGroupCode("");
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit report");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCampers = campers.filter(c => {
    const name = `${c.first_name} ${c.last_name}`.toLowerCase();
    return name.includes(camperSearch.toLowerCase());
  });

  // ── Group picker: build a sorted, grouped list of all camper groups. ──
  // Buckets reflect the real division map: Mariners = B/G 01–11, Navigators =
  // B/G 12–15, Skippers = N##, plus CIT. Matches server's _division_for_group_code.
  const groupBuckets = (() => {
    if (!useGroupPicker) return [];
    const codes = new Set();
    campers.forEach(c => {
      const gc = (c.group_code || "").trim();
      if (gc) codes.add(gc);
    });
    const sorted = Array.from(codes).sort();
    const bucketOf = (gc) => {
      if (/^[BG](0[1-9]|1[01])$/.test(gc)) return "Mariners";
      if (/^[BG]1[2-5]$/.test(gc)) return "Navigators";
      if (/^N(0[1-9]|1[0-6])$/.test(gc)) return "Skippers";
      if (gc === "CIT") return "CIT";
      return "Other";
    };
    const map = {};
    sorted.forEach(gc => {
      const b = bucketOf(gc);
      if (!map[b]) map[b] = [];
      map[b].push(gc);
    });
    const order = ["Skippers", "Mariners", "Navigators", "CIT", "Other"];
    return order.filter(k => map[k]).map(k => ({ label: k, codes: map[k] }));
  })();

  const campersInActiveGroup = useGroupPicker && activeGroupCode
    ? activeRoster
        .filter(c => (c.status || "").toLowerCase() !== "absent")
        .filter(c => {
          if (!camperSearch.trim()) return true;
          const name = `${c.first_name} ${c.last_name}`.toLowerCase();
          return name.includes(camperSearch.toLowerCase());
        })
        .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
    : [];

  const groupCodeBadgeColor = (gc) => {
    if (/^[BG]1[2-5]$/.test(gc)) return "bg-violet-100 text-violet-800 border-violet-200"; // Navigators
    if (gc?.startsWith("B")) return "bg-blue-100 text-blue-800 border-blue-200"; // Mariners boys
    if (gc?.startsWith("G")) return "bg-pink-100 text-pink-800 border-pink-200"; // Mariners girls
    if (gc?.startsWith("N")) return "bg-amber-100 text-amber-900 border-amber-200"; // Skippers
    if (gc === "CIT") return "bg-purple-100 text-purple-800 border-purple-200";
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center space-y-4">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
          <h2 className="font-heading text-xl font-bold">Report Submitted</h2>
          <p className="text-muted-foreground">Preparing next report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="incident-report-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1" data-testid="incident-back-btn">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-xs font-medium">Dashboard</span>
        </Button>
        <div>
          <h2 className="font-heading font-bold text-lg">Incident Reports</h2>
          <p className="text-sm text-muted-foreground">{group.code} — {group.name}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2" data-testid="incident-tabs">
        <button onClick={() => setTab("new")} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${tab === "new" ? "bg-red-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid="incident-tab-new">
          New Report
        </button>
        <button onClick={() => { setTab("past"); fetchPastReports(); }} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-1.5 ${tab === "past" ? "bg-red-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid="incident-tab-past">
          <FileText className="w-3.5 h-3.5" /> Past Reports {pastReports.length > 0 && <Badge className="bg-red-100 text-red-800 text-[10px] px-1.5">{pastReports.length}</Badge>}
        </button>
      </div>

      {/* Past Reports Tab */}
      {tab === "past" && (
        <div className="space-y-3" data-testid="incident-past-reports">
          {pastLoading && <p className="text-sm text-muted-foreground text-center py-8">Loading reports...</p>}
          {!pastLoading && pastReports.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No incident reports filed yet for {group.code}.</p>
            </div>
          )}
          {!pastLoading && pastReports.map(r => {
            const isExpanded = expandedReport === r.id;
            return (
              <Card key={r.id} className={`cursor-pointer transition-shadow hover:shadow-md ${r.great_day ? "border-green-200" : "border-red-200"}`} data-testid={`incident-card-${r.id}`}>
                <CardContent className="p-0">
                  <button onClick={() => setExpandedReport(isExpanded ? null : r.id)} className="w-full text-left p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={r.great_day ? "bg-green-100 text-green-800 text-[10px]" : "bg-red-100 text-red-800 text-[10px]"}>
                            {r.great_day ? "Great Day" : r.incident_type || "Incident"}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{r.date}</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {r.great_day ? (r.great_day_notes || "Great day reported") : (r.camper_names || []).join(", ") || "No campers listed"}
                        </p>
                        {r.ai_summary && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{r.ai_summary}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <a href={`${API}/incident-reports/${r.id}/pdf`} onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-gray-600" data-testid={`incident-pdf-${r.id}`}>
                          <Download className="w-4 h-4" />
                        </a>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t px-4 pb-4 pt-3 space-y-3 bg-gray-50/50">
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div><span className="font-bold text-gray-600">Date:</span> <span className="text-gray-800">{r.date}</span></div>
                        <div><span className="font-bold text-gray-600">Type:</span> <span className="text-gray-800">{r.great_day ? "Great Day" : r.incident_type}</span></div>
                        <div className="col-span-2"><span className="font-bold text-gray-600">Campers:</span> <span className="text-gray-800">{(r.camper_names || []).join(", ") || "—"}</span></div>
                      </div>
                      {r.great_day ? (
                        r.great_day_notes && <div><p className="text-xs font-bold text-gray-600 mb-1">Notes:</p><p className="text-sm text-gray-800 bg-white rounded-lg p-3 border">{r.great_day_notes}</p></div>
                      ) : (
                        <>
                          {r.description && <div><p className="text-xs font-bold text-gray-600 mb-1">Description:</p><p className="text-sm text-gray-800 bg-white rounded-lg p-3 border">{r.description}</p></div>}
                          {r.actions_taken?.length > 0 && <div><p className="text-xs font-bold text-gray-600 mb-1">Actions Taken:</p><div className="flex flex-wrap gap-1">{r.actions_taken.map((a, i) => <Badge key={i} variant="outline" className="text-[10px]">{a}</Badge>)}</div></div>}
                          {r.action_details && <div><p className="text-xs font-bold text-gray-600 mb-1">Action Details:</p><p className="text-sm text-gray-800 bg-white rounded-lg p-3 border">{r.action_details}</p></div>}
                          {r.parent_contact_detail && <div><p className="text-xs font-bold text-gray-600 mb-1">Parent Contact:</p><p className="text-sm text-gray-800 bg-white rounded-lg p-3 border">{r.parent_contact_detail}</p></div>}
                          {r.followup?.length > 0 && <div><p className="text-xs font-bold text-gray-600 mb-1">Follow-up:</p><div className="flex flex-wrap gap-1">{r.followup.map((f, i) => <Badge key={i} variant="outline" className="text-[10px]">{f}</Badge>)}</div></div>}
                          {r.followup_details && <div><p className="text-xs font-bold text-gray-600 mb-1">Follow-up Details:</p><p className="text-sm text-gray-800 bg-white rounded-lg p-3 border">{r.followup_details}</p></div>}
                          {r.ai_summary && <div><p className="text-xs font-bold text-gray-600 mb-1">AI Summary:</p><p className="text-sm text-gray-800 bg-blue-50 rounded-lg p-3 border border-blue-200 italic">{r.ai_summary}</p></div>}
                        </>
                      )}
                      <div className="flex items-center gap-1 text-[10px] text-gray-400 pt-1">
                        <Clock className="w-3 h-3" /> Submitted {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* New Report Tab */}
      {tab === "new" && (
      <div className="space-y-4">

      {/* Date */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-4">
            <Label className="font-semibold text-sm whitespace-nowrap">Date:</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
              className="w-44"
              data-testid="incident-date"
            />
          </div>
          {!useGroupPicker && (
            <div className="flex items-center gap-4 pt-1 border-t">
              <Label className="font-semibold text-sm whitespace-nowrap text-cyan-700">Roster Date:</Label>
              <input
                type="date"
                min="2026-06-29"
                max="2026-08-20"
                value={rosterDate}
                onChange={(e) => setRosterDate(e.target.value)}
                className="px-2 py-1 text-sm border border-cyan-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                data-testid="incident-roster-date"
              />
              <span className="text-xs text-cyan-600">{campers.length} camper{campers.length !== 1 ? "s" : ""}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Great Day Toggle */}
      <Card className={`transition-colors ${form.great_day ? "border-green-300 bg-green-50/50" : ""}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={form.great_day}
              onCheckedChange={(checked) => setForm(prev => ({ ...prev, great_day: !!checked }))}
              id="great-day"
              data-testid="great-day-checkbox"
            />
            <Label htmlFor="great-day" className="font-heading font-semibold text-base cursor-pointer flex items-center gap-2">
              <CheckCircle className={`w-5 h-5 ${form.great_day ? "text-green-600" : "text-muted-foreground"}`} />
              GREAT DAY
            </Label>
          </div>
          {form.great_day && (
            <Textarea
              placeholder="Optional: Notes about the great day..."
              value={form.great_day_notes}
              onChange={(e) => setForm(prev => ({ ...prev, great_day_notes: e.target.value }))}
              className="min-h-[60px]"
              data-testid="great-day-notes"
            />
          )}
        </CardContent>
      </Card>

      {/* Incident Form — only show if NOT great day */}
      {!form.great_day && (
        <>
          {/* Specialty (only for specialist mode) */}
          {isSpecialist && (
            <Card className="border-emerald-200 bg-emerald-50/40">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base text-emerald-800">Your Specialty</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  placeholder="Specialty"
                  className="bg-white"
                  data-testid="incident-specialty-input"
                />
                <p className="text-xs text-emerald-700 mt-1">This appears in the supervisor&apos;s notification.</p>
              </CardContent>
            </Card>
          )}

          {/* Select Campers */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base">Select Camper(s)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {selectedCampers.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedCampers.map(c => (
                    <Badge key={c.id} className="text-xs bg-primary-100 text-primary-800 border-primary-200 gap-1 pr-1" data-testid={`selected-camper-${c.id}`}>
                      {useGroupPicker && c.group_code && (
                        <span className={`text-[10px] font-mono px-1 rounded ${groupCodeBadgeColor(c.group_code)}`}>{c.group_code}</span>
                      )}
                      {c.first_name} {c.last_name}
                      <button onClick={() => toggleCamper(c)} className="ml-1 hover:bg-primary-200 rounded-full p-0.5">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {useGroupPicker ? (
                <>
                  {/* Group picker — one row per division bucket */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground">Pick a group first:</p>
                    {groupBuckets.map(bucket => (
                      <div key={bucket.label} className="space-y-1" data-testid={`group-bucket-${bucket.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{bucket.label}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {bucket.codes.map(gc => {
                            const isActive = activeGroupCode === gc;
                            return (
                              <button
                                key={gc}
                                type="button"
                                onClick={() => { setActiveGroupCode(gc); setCamperSearch(""); }}
                                className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-all ${
                                  isActive
                                    ? "bg-emerald-600 text-white border-emerald-700 shadow-sm"
                                    : `${groupCodeBadgeColor(gc)} hover:opacity-80`
                                }`}
                                data-testid={`specialist-group-pick-${gc}`}
                              >
                                {gc}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Campers in the active group */}
                  {activeGroupCode && (
                    <div className="border-t pt-3 space-y-2" data-testid="specialist-camper-picker">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold">
                          Campers in <span className={`px-2 py-0.5 rounded font-mono ${groupCodeBadgeColor(activeGroupCode)}`}>{activeGroupCode}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {activeRosterLoading ? "Loading…" : `(${campersInActiveGroup.length} on ${form.date} roster)`}
                          </span>
                        </p>
                        <Input
                          placeholder="Search…"
                          value={camperSearch}
                          onChange={(e) => setCamperSearch(e.target.value)}
                          className="text-xs h-8 w-40"
                          data-testid="specialist-camper-search"
                        />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-56 overflow-y-auto">
                        {activeRosterLoading ? (
                          <p className="col-span-3 text-xs text-muted-foreground text-center py-4">Loading roster…</p>
                        ) : campersInActiveGroup.length === 0 ? (
                          <p className="col-span-3 text-xs text-muted-foreground text-center py-4">
                            No campers on roster for {form.date}
                          </p>
                        ) : campersInActiveGroup.map(c => {
                          const isSelected = selectedCampers.some(sc => sc.id === c.id);
                          return (
                            <button
                              key={c.id}
                              onClick={() => toggleCamper(c)}
                              className={`text-left text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
                                isSelected ? "bg-primary-100 border-primary-300 font-semibold" : "hover:bg-muted/50 border-transparent"
                              }`}
                              data-testid={`camper-option-${c.id}`}
                            >
                              {c.first_name} {c.last_name}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Reflects today&apos;s daily roster (flex days + absent kids filtered). Pick another group above to add campers from a different group.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Search campers..."
                      value={camperSearch}
                      onChange={(e) => setCamperSearch(e.target.value)}
                      className="text-sm flex-1"
                      data-testid="camper-search"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 text-xs whitespace-nowrap"
                      onClick={() => {
                        const allSelected = filteredCampers.length > 0 && filteredCampers.every(c => selectedCampers.some(sc => sc.id === c.id));
                        if (allSelected) {
                          const ids = new Set(filteredCampers.map(c => c.id));
                          setSelectedCampers(prev => prev.filter(c => !ids.has(c.id)));
                        } else {
                          setSelectedCampers(prev => {
                            const existing = new Set(prev.map(c => c.id));
                            return [...prev, ...filteredCampers.filter(c => !existing.has(c.id))];
                          });
                        }
                      }}
                      data-testid="select-all-campers-btn"
                    >
                      {filteredCampers.length > 0 && filteredCampers.every(c => selectedCampers.some(sc => sc.id === c.id)) ? "Clear All" : "Select All"}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto">
                    {filteredCampers.map(c => {
                      const isSelected = selectedCampers.some(sc => sc.id === c.id);
                      return (
                        <button
                          key={c.id}
                          onClick={() => toggleCamper(c)}
                          className={`text-left text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
                            isSelected ? "bg-primary-100 border-primary-300 font-semibold" : "hover:bg-muted/50 border-transparent"
                          }`}
                          data-testid={`camper-option-${c.id}`}
                        >
                          {c.first_name} {c.last_name}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Incident Type */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base">Type of Incident</CardTitle>
            </CardHeader>
            <CardContent className="flex gap-3">
              <Button
                variant={form.incident_type === "Medical" ? "default" : "outline"}
                onClick={() => setForm(prev => ({ ...prev, incident_type: "Medical" }))}
                className="flex-1"
                data-testid="type-medical"
              >
                <Stethoscope className="w-4 h-4 mr-2" />Medical
              </Button>
              <Button
                variant={form.incident_type === "Behavioral" ? "default" : "outline"}
                onClick={() => setForm(prev => ({ ...prev, incident_type: "Behavioral" }))}
                className="flex-1"
                data-testid="type-behavioral"
              >
                <Brain className="w-4 h-4 mr-2" />Behavioral
              </Button>
            </CardContent>
          </Card>

          {/* Description */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base">Description of Incident</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Describe what happened..."
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                className="min-h-[100px]"
                data-testid="incident-description"
              />
            </CardContent>
          </Card>

          {/* Immediate Actions Taken */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base">Immediate Actions Taken</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-3">
                {["Sent to Nurse", "Supervisor Notified", "Parent Contacted"].map(action => (
                  <div key={action} className="flex items-center gap-2">
                    <Checkbox
                      checked={form.actions_taken.includes(action)}
                      onCheckedChange={() => toggleAction(action)}
                      id={`action-${action}`}
                      data-testid={`action-${action.toLowerCase().replace(/ /g, '-')}`}
                    />
                    <Label htmlFor={`action-${action}`} className="text-sm cursor-pointer">{action}</Label>
                  </div>
                ))}
              </div>
              {form.actions_taken.includes("Parent Contacted") && (
                <div className="flex gap-3 pl-6">
                  {["Left Message", "Spoke"].map(opt => (
                    <div key={opt} className="flex items-center gap-2">
                      <Checkbox
                        checked={form.parent_contact_detail === opt}
                        onCheckedChange={() => setForm(prev => ({ ...prev, parent_contact_detail: prev.parent_contact_detail === opt ? "" : opt }))}
                        id={`parent-${opt}`}
                        data-testid={`parent-${opt.toLowerCase().replace(/ /g, '-')}`}
                      />
                      <Label htmlFor={`parent-${opt}`} className="text-sm cursor-pointer">{opt}</Label>
                    </div>
                  ))}
                </div>
              )}
              <Textarea
                placeholder="Details of actions taken..."
                value={form.action_details}
                onChange={(e) => setForm(prev => ({ ...prev, action_details: e.target.value }))}
                className="min-h-[60px]"
                data-testid="action-details"
              />
            </CardContent>
          </Card>

          {/* Follow-Up & Resolution */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base">Follow-Up & Resolution</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-3">
                {["Situation Resolved", "Parent Follow-Up Completed"].map(item => (
                  <div key={item} className="flex items-center gap-2">
                    <Checkbox
                      checked={form.followup.includes(item)}
                      onCheckedChange={() => toggleFollowup(item)}
                      id={`followup-${item}`}
                      data-testid={`followup-${item.toLowerCase().replace(/ /g, '-')}`}
                    />
                    <Label htmlFor={`followup-${item}`} className="text-sm cursor-pointer">{item}</Label>
                  </div>
                ))}
              </div>
              <Textarea
                placeholder="Follow-up details..."
                value={form.followup_details}
                onChange={(e) => setForm(prev => ({ ...prev, followup_details: e.target.value }))}
                className="min-h-[60px]"
                data-testid="followup-details"
              />
            </CardContent>
          </Card>
        </>
      )}

      {/* Submit */}
      <Button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full h-12 text-base"
        data-testid="submit-incident"
      >
        {submitting ? "Submitting..." : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Submit Report
          </>
        )}
      </Button>
      </div>
      )}
    </div>
  );
}
